import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
// Utilities
import { useSnackbar } from '../../utils/useSnackbar';
import { ResetPasswordSchema } from '../../Validation/ValidationRule';
import { resetPassword, validateResetToken } from '../../utils/api/user_apis';
import { STATUS } from '../../utils/api/sharedfunction';
// components
import FormWrapper from '../../components/Forms/FormWrapper';
import { PasswordField } from '../../components/PasswordField';
import LegalConsentCheckbox from '../../components/consent/LegalConsentCheckbox';
import { CONSENT_COPY } from '../../constants/consentCopy';
import TokenStatusView from '../../components/TokenStatus/TokenStatusView';
import { tokenCodeToStatus } from '../../components/TokenStatus/tokenStatusCopy';
import DEFAULT_ROUTES from '../../constants/routes';
import '../../components/Forms/FormWrapper.css';

const PAGE_STATE = {
  VERIFYING: 'verifying',
  VALID: 'valid',
  MISSING: 'missing',
  INVALID: 'invalid',
  EXPIRED: 'expired',
  ERROR: 'error',
};

const PasswordAction = ({ type = 'reset' }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const isSetPassword = type === 'set';
  const config = {
    title: isSetPassword ? 'Set Password' : 'Reset Password',
    buttonText: isSetPassword ? 'Set Password' : 'Reset Password',
    successMessage: isSetPassword ? 'Password set successfully!' : 'Password reset successfully!',
  };

  const [token, setToken] = useState('');
  const [pageState, setPageState] = useState(PAGE_STATE.VERIFYING);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [consentMeta, setConsentMeta] = useState({ ready: false });
  const [consentError, setConsentError] = useState(null);

  const handleConsentChange = useCallback((meta) => {
    setConsentAgreed(meta.agreed);
    setConsentMeta(meta);
    if (meta.agreed) {
      setConsentError(null);
    }
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');

    if (!tokenParam || !tokenParam.trim()) {
      setPageState(PAGE_STATE.MISSING);
      return;
    }

    let cancelled = false;
    setToken(tokenParam);
    setPageState(PAGE_STATE.VERIFYING);

    validateResetToken({ token: tokenParam })
      .then((response) => {
        if (cancelled) return;
        if (response.status === STATUS.SUCCESS) {
          setPageState(PAGE_STATE.VALID);
          return;
        }
        const status = tokenCodeToStatus(response.code);
        if (status === 'missing') setPageState(PAGE_STATE.MISSING);
        else if (status === 'expired') setPageState(PAGE_STATE.EXPIRED);
        else setPageState(PAGE_STATE.INVALID);
      })
      .catch(() => {
        if (!cancelled) {
          setPageState(PAGE_STATE.ERROR);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [location]);

  const onSubmit = async (data) => {
    if (!consentAgreed) {
      setConsentError(CONSENT_COPY.accountValidationError);
      return;
    }
    if (!consentMeta.ready) {
      setConsentError(consentMeta.error || 'Legal documents are not available. Please try again later.');
      return;
    }

    try {
      const response = await resetPassword({
        token: token,
        newPassword: data.newPassword,
        tosLegalDocumentVersionId: consentMeta.tosLegalDocumentVersionId,
        privacyLegalDocumentVersionId: consentMeta.privacyLegalDocumentVersionId,
      });

      if (response.status === STATUS.SUCCESS) {
        showSnackbar(config.successMessage, 'success');
        reset();
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        showSnackbar(response.error || 'Failed to reset password. Please try again.', 'error');
      }
    } catch (error) {
      console.error(error);
      showSnackbar(error.error || 'Failed to reset password. Please try again.', 'error');
    }
  };

  const goToLogin = () => navigate(DEFAULT_ROUTES.LOGIN);

  const renderTokenStatus = (status, extraDescription) => (
    <Box className="box-container">
      <Box className="box-card">
        <Box className="logo-container">
          <img src="/image.png" alt="QA Path Logo" />
        </Box>
        <TokenStatusView
          status={status}
          description={extraDescription}
          primaryAction={{ label: 'Back to Login', onClick: goToLogin }}
        />
      </Box>
    </Box>
  );

  if (pageState === PAGE_STATE.VERIFYING) {
    return (
      <Box className="box-container">
        <Box className="box-card">
          <Box className="logo-container">
            <img src="/image.png" alt="QA Path Logo" />
          </Box>
          <p style={{ textAlign: 'center', color: '#64748b' }}>Verifying your link…</p>
        </Box>
      </Box>
    );
  }

  if (pageState === PAGE_STATE.MISSING) {
    return renderTokenStatus('missing');
  }

  if (pageState === PAGE_STATE.INVALID) {
    const description = isSetPassword
      ? 'This set-password link is invalid or has already been used. Please contact your administrator for a new invite.'
      : undefined;
    return renderTokenStatus('invalid', description);
  }

  if (pageState === PAGE_STATE.EXPIRED) {
    const description = isSetPassword
      ? 'This set-password link has expired. Please contact your administrator for a new invite.'
      : undefined;
    return renderTokenStatus('expired', description);
  }

  if (pageState === PAGE_STATE.ERROR) {
    return renderTokenStatus(
      'invalid',
      'We could not verify this link. Please try again or request a new one.'
    );
  }

  return (
    <Box>
      <FormWrapper
        title={config.title}
        onSubmit={handleSubmit(onSubmit)}
        loading={false}
        error=""
        buttonName={config.buttonText}
        submitDisabled={!isValid || !consentAgreed || !consentMeta.ready}
      >
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              field={field}
              label="New Password"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              field={field}
              label="Confirm Password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />
          )}
        />
        <Box sx={{ mt: 2 }}>
          <LegalConsentCheckbox
            checked={consentAgreed}
            onChange={handleConsentChange}
            error={consentError}
            id="set-password-consent"
          />
        </Box>
      </FormWrapper>
    </Box>
  );
};

export default PasswordAction;
